import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { generateServiceSchema, generateBreadcrumbSchema, generateFAQSchema } from '../utils/schema';
import { SERVICE_CONTENT } from '../content/serviceContent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback meta data per slug (used when API/DB is empty or stale)
const FALLBACK_META = {
  'rodent-control': {
    name: 'Rodent Control',
    meta_title: 'Rodent Control NYC | Rat & Mouse Exterminator',
    meta_description: 'Need rodent control in NYC? Reclaim Pest Control provides rat and mouse extermination, inspection, exclusion guidance, and prevention for homes, apartments, and commercial properties.'
  },
  'cockroach-extermination': {
    name: 'Cockroach Extermination',
    meta_title: 'Roach Exterminator NYC | Cockroach Control',
    meta_description: 'Professional cockroach extermination in NYC for apartments, homes, restaurants, and buildings. Call Reclaim Pest Control for roach inspection and treatment.'
  },
  'bed-bug-removal': {
    name: 'Bed Bug Treatment',
    meta_title: 'Bed Bug Treatment NYC | Bed Bug Exterminator',
    meta_description: 'Bed bug treatment in NYC for apartments, homes, and multi-unit properties. Reclaim Pest Control provides inspection, treatment, and follow-up guidance.'
  },
  'ant-extermination': {
    name: 'Ant Extermination',
    meta_title: 'Ant Extermination NYC | Ant Control & Removal',
    meta_description: 'Ant extermination in NYC. Carpenter, pavement, pharaoh, and sugar ants. Targeted bait and perimeter treatments for homes, apartments, and businesses.'
  },
  'wasp-removal': {
    name: 'Wasp & Bee Removal',
    meta_title: 'Wasp & Bee Removal NYC | Hornet Nest Removal',
    meta_description: 'Safe wasp, hornet, and bee nest removal from yards, attics, soffits, walls, and eaves across NYC and Long Island. Same-day service available.'
  },
  'flea-control': {
    name: 'Flea Control',
    meta_title: 'Flea Control NYC | Flea Extermination for Homes with Pets',
    meta_description: 'Pet-safe flea elimination for NYC homes with cats and dogs. Multi-stage treatment to break the flea life cycle.'
  },
  'termite-control': {
    name: 'Termite Control',
    meta_title: 'Termite Control NYC | Termite Inspection & Treatment',
    meta_description: 'Termite inspections, liquid treatments, and bait systems for NYC homes, businesses, and real estate transactions across NYC and Long Island.'
  },
  'general-pest-control': {
    name: 'General Pest Control',
    meta_title: 'General Pest Control NYC | Quarterly Pest Programs',
    meta_description: 'Quarterly and one-time pest control for NYC homes and businesses. Spiders, silverfish, moths, flies, crickets, and seasonal occasional invaders.'
  }
};

// Service-specific FAQs (rendered as FAQPage schema + visible content)
const SERVICE_FAQS = {
  'rodent-control': [
    { question: 'How do I know if I have rats or mice?', answer: 'Common signs include droppings near walls or in cabinets, gnaw marks on packaging or wood, scratching sounds in walls or ceilings, and greasy rub marks along baseboards. A licensed Reclaim Pest Control technician can confirm and identify the species.' },
    { question: 'Do you provide rodent control for apartments in NYC?', answer: 'Yes. We treat apartments, condos, co-ops, and multi-unit buildings throughout NYC, working with both tenants and property managers.' },
    { question: 'Is your rodent treatment safe for kids and pets?', answer: 'We use tamper-resistant bait stations and targeted methods designed to keep pets and children safe. Our technicians explain placement and precautions during the visit.' },
    { question: 'How fast can you come for a rodent issue?', answer: 'Reclaim Pest Control offers same-day rodent service across most of NYC and Long Island. Call 646-209-9955 to schedule.' }
  ],
  'cockroach-extermination': [
    { question: 'How long does it take to get rid of cockroaches?', answer: 'Most homes see major reduction within 1–2 weeks of treatment. Heavy infestations or multi-unit buildings may require follow-up visits with gel baiting and IGR (insect growth regulator) treatments.' },
    { question: 'Can you treat roaches in NYC apartments and restaurants?', answer: 'Yes. We specialize in roach treatment for NYC apartments, restaurants, kitchens, and food establishments. Treatments comply with NYC health department standards.' },
    { question: 'Will I need to leave my home during treatment?', answer: 'For most gel-baiting and crack-and-crevice treatments, no. Our technician will explain any specific precautions for your treatment plan.' },
    { question: 'Do you handle German, American, and Oriental roaches?', answer: 'Yes. We identify the roach species and target the correct harborage and reproduction cycle for that species.' }
  ],
  'bed-bug-removal': [
    { question: 'Can you treat bed bugs in apartments and multi-unit buildings?', answer: 'Yes. We routinely treat bed bugs in apartments, condos, co-ops, and multi-family buildings throughout NYC. We coordinate with tenants and property managers when needed.' },
    { question: 'How long does a bed bug treatment take?', answer: 'Most apartments take 2–4 hours per treatment, with a follow-up inspection 2–3 weeks later to confirm elimination.' },
    { question: 'What do I need to do before a bed bug treatment?', answer: 'Our technician will provide a prep checklist — typically laundering bedding, decluttering, and providing access to mattresses, furniture, and baseboards.' },
    { question: 'Do you guarantee your bed bug treatments?', answer: 'Yes. We provide follow-up inspections and stand behind our treatments with a guarantee.' }
  ],
  'ant-extermination': [
    { question: 'Do you handle carpenter ants?', answer: 'Yes. We treat carpenter ants, pavement ants, sugar ants, pharaoh ants, and odorous house ants using targeted baits and direct nest treatments.' },
    { question: 'Why do ants keep coming back?', answer: 'Most ant problems return when the colony or food source is not fully eliminated. Our treatments target the entire colony, not just visible workers.' },
    { question: 'Are your ant treatments safe for pets?', answer: 'Yes. We use targeted bait stations and treatments that minimize pet exposure. Our technician will explain placement and timing.' }
  ],
  'wasp-removal': [
    { question: 'Is wasp removal safe for my family and pets?', answer: 'Yes. Our technicians wear protective equipment and treat the nest during low-activity hours. We then remove the nest and seal entry points.' },
    { question: 'Do you remove wasp nests in attics and walls?', answer: 'Yes. We remove wasp, hornet, and bee nests from yards, attics, soffits, walls, eaves, and other hard-to-reach areas.' },
    { question: 'How fast can you remove a wasp nest?', answer: 'Same-day wasp nest removal is usually available. Call 646-209-9955.' }
  ],
  'flea-control': [
    { question: 'Are flea treatments safe for pets?', answer: 'Yes. We use pet-safe flea treatments and recommend keeping pets out of treated rooms only until surfaces are dry.' },
    { question: 'How long does it take to get rid of fleas?', answer: 'Most homes see major flea reduction within 1–2 weeks. We typically schedule a follow-up to break the flea life cycle completely.' }
  ],
  'termite-control': [
    { question: 'Do you offer termite inspections?', answer: 'Yes. We provide termite inspections for homes, businesses, and real estate transactions throughout NYC and Long Island.' },
    { question: 'What kind of termite treatments do you use?', answer: 'We use liquid termiticide treatments and bait station systems based on the property and severity of activity.' }
  ],
  'general-pest-control': [
    { question: 'What pests does general pest control cover?', answer: 'Our general pest control program covers common household pests including ants, spiders, silverfish, moths, flies, and crickets. Quarterly programs help prevent infestations year-round.' },
    { question: 'Do you offer quarterly pest control?', answer: 'Yes. We offer quarterly and one-time pest control programs for homes and businesses.' }
  ]
};

const DEFAULT_FAQS = [
  { question: 'How fast can you come?', answer: 'Reclaim Pest Control offers same-day service across most of NYC and Long Island. Call 646-209-9955 to schedule.' },
  { question: 'Do you serve all NYC boroughs?', answer: 'Yes. We service Queens, Brooklyn, Manhattan, Bronx, and Staten Island, plus Nassau County, Suffolk County, and Westchester County.' },
  { question: 'Do you provide commercial pest control?', answer: 'Yes. We serve restaurants, food establishments, offices, retail spaces, warehouses, and property management companies.' }
];

const ServiceDetail = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFAQ, setOpenFAQ] = useState(0);

  useEffect(() => {
    setLoading(true);
    setService(null);
    axios.get(`${API_URL}/api/services/${slug}`)
      .then(res => {
        setService(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error:', err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <div className="container mx-auto px-4 py-16 text-center">Loading...</div>;

  // Rich, unique content for this service (always present, not DB-dependent)
  const content = SERVICE_CONTENT[slug];
  const fallback = FALLBACK_META[slug];

  // If no content AND no API service AND no fallback, show 404
  if (!content && !service && !fallback) {
    return <div className="container mx-auto px-4 py-16 text-center" data-testid="service-not-found">Service not found</div>;
  }

  // Compose unified data: API data (if present) wins for dynamic fields; static content wins for SEO-critical fields
  const displayName = (service && service.name) || (fallback && fallback.name) || 'Pest Control';
  const metaTitle = (content && content.h1) || (service && service.meta_title) || (fallback && fallback.meta_title) || 'Pest Control NYC';
  const metaDescription = (service && service.meta_description) || (fallback && fallback.meta_description) || '';
  const h1 = (content && content.h1) || metaTitle;
  const heroSub = (content && content.hero_sub) || (service && service.short_description) || '';

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' },
    { name: displayName, url: `/services/${slug}` }
  ];

  const faqs = SERVICE_FAQS[slug] || DEFAULT_FAQS;

  // Service schema (works without API data)
  const serviceForSchema = service || {
    name: displayName,
    short_description: heroSub,
    full_description: content && content.sections ? content.sections.map(s => s.body).join(' ').slice(0, 500) : metaDescription
  };

  return (
    <div>
      <SEO 
        title={metaTitle}
        description={metaDescription}
        keywords={`${displayName}, ${displayName} NYC, ${displayName} Queens NY, ${displayName} Brooklyn, ${displayName} Manhattan, ${displayName} Bronx, exterminator NYC`}
        schema={[generateServiceSchema(serviceForSchema), generateBreadcrumbSchema(breadcrumbs), generateFAQSchema(faqs)]}
        canonical={`https://www.reclaimpestcontrol.com/services/${slug}`}
      />
      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4">
          <nav className="text-sm mb-4" aria-label="Breadcrumb">
            <a href="/" className="hover:underline">Home</a> / <a href="/services" className="hover:underline">Services</a> / {displayName}
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-testid="service-h1">{h1}</h1>
          {heroSub && <p className="text-xl max-w-3xl" data-testid="service-hero-sub">{heroSub}</p>}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href="tel:6462099955" className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition inline-flex items-center justify-center" data-testid="service-hero-call">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" /></svg>
              Call 646-209-9955
            </a>
            <Link to="/contact" className="bg-white text-green-700 hover:bg-gray-100 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition inline-flex items-center justify-center border-2 border-white">
              Request a Pest Inspection
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2">
              {/* Service Image */}
              {(() => {
                const imageMap = {
                  'rodent-control': '/images/services/rodent.jpg',
                  'bed-bug-removal': '/images/services/bedbug.jpg',
                  'cockroach-extermination': '/images/services/cockroach.jpg',
                  'termite-control': '/images/services/termite.jpg',
                  'ant-extermination': '/images/services/ant.jpg',
                  'wasp-removal': '/images/services/wasp.jpg',
                  'flea-control': '/images/services/flea.jpg',
                  'spider-control': '/images/services/spider.jpg',
                  'mosquito-control': '/images/services/mosquito.jpg',
                  'fly-control': '/images/services/fly.jpg',
                  'pest-inspection': '/images/services/inspection.jpg',
                  'rodent-proofing': '/images/services/rodent-proofing.png',
                  'general-pest-control': '/images/services/general.jpg'
                };
                return (
                  <img 
                    src={imageMap[slug] || '/images/services/general.jpg'} 
                    alt={displayName}
                    className="w-full rounded-lg shadow-lg mb-8"
                  />
                );
              })()}
              
              <div className="prose max-w-none" data-testid="service-rich-content">
                {/* Long-form unique content from static SERVICE_CONTENT (works without DB) */}
                {content && content.sections && content.sections.map((s, idx) => (
                  <div key={idx} className="mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold mb-3 text-gray-900">{s.h2}</h2>
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed">{s.body}</p>
                  </div>
                ))}

                {/* Service-specific CTA from content */}
                {content && content.cta && (
                  <div className="bg-green-50 border-l-4 border-green-600 p-5 rounded my-8" data-testid="service-mid-cta">
                    <p className="text-base md:text-lg font-semibold text-gray-900 mb-3">{content.cta}</p>
                    <a href="tel:6462099955" className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3 rounded-lg transition" data-testid="service-mid-call-btn">
                      Call 646-209-9955
                    </a>
                  </div>
                )}

                {/* Fallback: full_description from API if no static content for this slug */}
                {!content && service && service.full_description && (
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">{service.full_description}</p>
                )}

                {service && service.features && service.features.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Service Highlights</h2>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {service && service.benefits && service.benefits.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Benefits</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="bg-blue-50 p-4 rounded-lg">
                          <p className="font-medium">{benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {service && service.process_steps && service.process_steps.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold mb-4">Our Process</h2>
                    <div className="space-y-4">
                      {service.process_steps.map((step, idx) => (
                        <div key={idx} className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 font-bold">{idx + 1}</div>
                          <p className="pt-1">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-green-600 text-white p-6 rounded-lg mb-6 sticky top-24">
                <h3 className="text-2xl font-bold mb-4">Get Help Now</h3>
                <p className="mb-6">24/7 Emergency Service Available</p>
                <a href="tel:6462099955" className="block bg-white text-green-600 text-center py-3 rounded-lg font-bold mb-4 hover:bg-gray-100 transition">📞 Call (646) 209-9955</a>
                <Link to="/contact" className="block bg-blue-600 text-center py-3 rounded-lg font-bold hover:bg-blue-700 transition">Request Free Quote</Link>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold mb-4">Other Services</h3>
                <ul className="space-y-2">
                  <li><Link to="/services/rodent-control" className="text-blue-600 hover:underline">Rodent Control</Link></li>
                  <li><Link to="/services/bed-bug-removal" className="text-blue-600 hover:underline">Bed Bug Removal</Link></li>
                  <li><Link to="/services/cockroach-extermination" className="text-blue-600 hover:underline">Cockroach Extermination</Link></li>
                  <li><Link to="/services/termite-control" className="text-blue-600 hover:underline">Termite Control</Link></li>
                  <li><Link to="/services" className="text-green-600 hover:underline font-semibold">View All Services →</Link></li>
                </ul>
              </div>

              {/* Service Areas - Internal Linking */}
              <div className="bg-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold mb-4">Service Areas</h3>
                <p className="text-sm text-gray-600 mb-3">We provide {displayName} in:</p>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/service-areas/queens-ny" className="text-blue-600 hover:underline">Queens, NY</Link></li>
                  <li><Link to="/service-areas/manhattan-ny" className="text-blue-600 hover:underline">Manhattan, NY</Link></li>
                  <li><Link to="/service-areas/brooklyn-ny" className="text-blue-600 hover:underline">Brooklyn, NY</Link></li>
                  <li><Link to="/service-areas/bronx-ny" className="text-blue-600 hover:underline">Bronx, NY</Link></li>
                  <li><Link to="/service-areas/nassau-county-ny" className="text-blue-600 hover:underline">Nassau County</Link></li>
                  <li><Link to="/service-areas/suffolk-county-ny" className="text-blue-600 hover:underline">Suffolk County</Link></li>
                  <li><Link to="/service-areas" className="text-green-600 hover:underline font-semibold">All Service Areas →</Link></li>
                </ul>
              </div>

              {/* Related Blog Posts */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Related Articles</h3>
                <ul className="space-y-3 text-sm">
                  {slug === 'rodent-control' && (
                    <li><Link to="/blog/rodent-control-tips-queens-ny" className="text-blue-600 hover:underline">Rodent Control Tips for Queens →</Link></li>
                  )}
                  {slug === 'bed-bug-removal' && (
                    <li><Link to="/blog/how-to-get-rid-of-bed-bugs-nyc" className="text-blue-600 hover:underline">NYC Bed Bug Control Guide →</Link></li>
                  )}
                  {slug === 'cockroach-extermination' && (
                    <li><Link to="/blog/cockroach-extermination-brooklyn-manhattan" className="text-blue-600 hover:underline">Cockroach Guide Brooklyn & Manhattan →</Link></li>
                  )}
                  {slug === 'termite-control' && (
                    <li><Link to="/blog/termite-inspection-long-island-nassau-suffolk" className="text-blue-600 hover:underline">Termite Inspection Long Island →</Link></li>
                  )}
                  <li><Link to="/blog" className="text-green-600 hover:underline font-semibold">All Blog Posts →</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              {displayName} — Frequently Asked Questions
            </h2>
            <div className="space-y-3" data-testid="service-faq-list">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFAQ(openFAQ === idx ? -1 : idx)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between font-semibold text-gray-900 hover:bg-gray-100 transition"
                    data-testid={`service-faq-toggle-${idx}`}
                  >
                    <span>{faq.question}</span>
                    <span className={`text-green-600 text-xl transition-transform ${openFAQ === idx ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openFAQ === idx && (
                    <div className="px-5 pb-5 text-gray-700 border-t border-gray-200">
                      <p className="pt-3">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a
                href="tel:6462099955"
                className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold px-7 py-3 rounded-lg transition"
                data-testid="service-faq-call-btn"
              >
                Call 646-209-9955 for {displayName}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hyper-Local CTA Section */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">{displayName} Near You</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {[
              { name: 'Queens', slug: 'queens-ny' },
              { name: 'Manhattan', slug: 'manhattan-ny' },
              { name: 'Brooklyn', slug: 'brooklyn-ny' },
              { name: 'Bronx', slug: 'bronx-ny' },
              { name: 'Staten Island', slug: 'staten-island-ny' },
              { name: 'Long Island', slug: 'long-island-ny' },
              { name: 'Nassau County', slug: 'nassau-county-ny' },
              { name: 'Suffolk County', slug: 'suffolk-county-ny' },
              { name: 'Westchester', slug: 'westchester-ny' },
              { name: 'Hempstead', slug: 'hempstead-ny' },
              { name: 'Flushing', slug: 'queens-ny' },
              { name: 'Astoria', slug: 'queens-ny' }
            ].map(area => (
              <Link 
                key={area.name}
                to={`/service-areas/${area.slug}`}
                className="bg-white p-3 rounded-lg text-center hover:bg-green-50 hover:border-green-500 border transition text-sm"
              >
                {displayName} in <span className="font-semibold text-green-600">{area.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;